import { useState, useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchMyCertifications, fetchRequiredCertifications } from 'modules/state-mgmt/workerData';
import { FilterCertificateStatus } from 'models/certifications';
import { prettifyErrorResponse } from 'utils';
import {
  Certification,
  RequiredCertificate,
  TabIndex,
  SortType,
  CertificationStatus,
  CertificationCounts,
} from '../types';

export const useCertificationList = () => {
  const dispatch = useAppDispatch();

  const { workerId, languageCode, myCertificationsData, isWorker } = useAppSelector(state => ({
    workerId: state.onboarding.id,
    languageCode: state.app?.languageCode,
    myCertificationsData: state.workerData.myCertifications,
    isWorker: state.user?.isWorker,
  }));

  const [certificationList, setCertificationList] = useState<Certification[]>([]);
  const [requiredCertificateList, setRequiredCertificateList] = useState<RequiredCertificate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(TabIndex.CURRENT);
  const [sortTypes, setSortTypes] = useState({
    [TabIndex.REQUIRED]: SortType.NAME_ASC,
    [TabIndex.EXPIRED]: SortType.NAME_ASC,
    [TabIndex.CURRENT]: SortType.NAME_ASC,
  });
  const [pages, setPages] = useState({
    [TabIndex.REQUIRED]: 1,
    [TabIndex.EXPIRED]: 1,
    [TabIndex.CURRENT]: 1,
  });
  const [projectIdsByTab, setProjectIdsByTab] = useState<{
    [key: number]: string[] | undefined;
  }>({
    [TabIndex.REQUIRED]: undefined,
    [TabIndex.EXPIRED]: undefined,
    [TabIndex.CURRENT]: undefined,
  });
  const [noRelatedProjectByTab, setNoRelatedProjectByTab] = useState<{
    [key: number]: boolean;
  }>({
    [TabIndex.REQUIRED]: false,
    [TabIndex.EXPIRED]: false,
    [TabIndex.CURRENT]: false,
  });
  const itemsPerPage = 15;
  const initializedRef = useRef(false);

  // Helper to map TabIndex to FilterCertificateStatus
  const getFilterStatus = (tabIndex: number): FilterCertificateStatus => {
    if (tabIndex === TabIndex.CURRENT) return FilterCertificateStatus.CURRENT;
    if (tabIndex === TabIndex.EXPIRED) return FilterCertificateStatus.EXPIRED;
    return FilterCertificateStatus.REQUIRED;
  };

  // Reusable method to fetch certifications with optional project filter
  const fetchCertificationData = async (
    filterStatus: FilterCertificateStatus,
    pageNum: number,
    sort: SortType,
    projectIdsFilter?: string[],
    noRelatedProject?: boolean,
  ) => {
    if (!workerId) return;

    try {
      // Use separate endpoint for required certifications
      if (filterStatus === FilterCertificateStatus.REQUIRED) {
        await dispatch(
          fetchRequiredCertifications({
            workerId,
            pageNumber: pageNum,
            pageSize: itemsPerPage,
            languageCode: languageCode || 'en',
            projectIds: projectIdsFilter,
            sortCriteria: sort,
          }),
        ).unwrap();
      } else {
        await dispatch(
          fetchMyCertifications({
            workerId,
            pageNumber: pageNum,
            pageSize: itemsPerPage,
            filterCertificateStatus: filterStatus,
            sortCriteria: sort,
            languageCode: languageCode || 'en',
            projectIds: projectIdsFilter,
            filterCertificateRelatedTo: noRelatedProject ? 2 : undefined,
          }),
        ).unwrap();
      }
    } catch (err) {
      const errorMessage = prettifyErrorResponse(err);
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
      throw err;
    }
  };

  // Initial data load - runs once on mount
  useEffect(() => {
    if (!workerId || initializedRef.current) return;

    initializedRef.current = true;
    setIsLoading(true);

    Promise.all([
      fetchCertificationData(FilterCertificateStatus.REQUIRED, 1, SortType.NAME_ASC),
      fetchCertificationData(FilterCertificateStatus.CURRENT, 1, SortType.NAME_ASC),
      fetchCertificationData(FilterCertificateStatus.EXPIRED, 1, SortType.NAME_ASC),
    ]).finally(() => {
      setIsLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transform API data to component format
  useEffect(() => {
    // Clear lists if no data available
    if (!myCertificationsData) {
      setCertificationList([]);
      setRequiredCertificateList([]);
      return;
    }

    // Handle Required tab - use API data
    if (activeTab === TabIndex.REQUIRED) {
      if (myCertificationsData.required) {
        const transformedData: RequiredCertificate[] = myCertificationsData.required.items.map((item: any) => ({
          id: item.projectId,
          projectId: item.projectId,
          projectName: item.projectName || '',
          certificationGroups: item.certificationGroups || [],
          status: CertificationStatus.REQUIRED,
        }));
        setRequiredCertificateList(transformedData);
      } else {
        setRequiredCertificateList([]);
      }
      setCertificationList([]);
      return;
    }

    // Handle Current tab - use API data only
    if (activeTab === TabIndex.CURRENT) {
      if (myCertificationsData.current) {
        const transformedData: Certification[] = myCertificationsData.current.items.map((item: any) => ({
          id: item.workerCertificateId,
          name: item.certificationName || '',
          certificationId: item.idNumber || '--',
          certificationTypeId: item.certificateId,
          description: item.description || '--',
          issueDate: item.completionDate || '',
          expirationDate: item.expirationDate || '',
          lastUpdatedAt: item.lastUpdatedAt || '',
          projectsRequired: item.projectNames?.map((proj: any) => proj.name) || [],
          status: CertificationStatus.CURRENT,
        }));
        setCertificationList(transformedData);
      } else {
        setCertificationList([]);
      }
      setRequiredCertificateList([]);
    }
    // Handle Expired tab - use API data only
    else if (activeTab === TabIndex.EXPIRED) {
      if (myCertificationsData.expired) {
        const transformedData: Certification[] = myCertificationsData.expired.items.map((item: any) => ({
          id: item.workerCertificateId,
          name: item.certificationName || '',
          certificationId: item.idNumber || '--',
          certificationTypeId: item.certificateId,
          description: item.description || '--',
          issueDate: item.completionDate || '',
          expirationDate: item.expirationDate || '',
          lastUpdatedAt: item.lastUpdatedAt || '',
          projectsRequired: item.projectNames?.map((proj: any) => proj.name) || [],
          status: CertificationStatus.EXPIRED,
          isNewCertificateAvailable: item.isNewCertificateAvailable,
        }));
        setCertificationList(transformedData);
      } else {
        setCertificationList([]);
      }
      setRequiredCertificateList([]);
    }
  }, [myCertificationsData, activeTab]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);

    setIsLoading(true);
    const filterToUse =
      projectIdsByTab[newValue] && projectIdsByTab[newValue]!.length > 0 ? projectIdsByTab[newValue] : undefined;
    const noRelatedProjectToUse = noRelatedProjectByTab[newValue] || false;
    fetchCertificationData(
      getFilterStatus(newValue),
      pages[newValue],
      sortTypes[newValue],
      filterToUse,
      noRelatedProjectToUse,
    ).finally(() => {
      setIsLoading(false);
    });
  };

  const handlePageChange = (value: number) => {
    setPages(prev => ({ ...prev, [activeTab]: value }));

    setIsLoading(true);
    setError(null);

    const filterToUse =
      projectIdsByTab[activeTab] && projectIdsByTab[activeTab]!.length > 0 ? projectIdsByTab[activeTab] : undefined;
    const noRelatedProjectToUse = noRelatedProjectByTab[activeTab] || false;
    fetchCertificationData(
      getFilterStatus(activeTab),
      value,
      sortTypes[activeTab],
      filterToUse,
      noRelatedProjectToUse,
    ).finally(() => {
      setIsLoading(false);
    });
  };

  const handleSortChange = (newSortType: SortType) => {
    setSortTypes(prev => ({
      ...prev,
      [activeTab]: newSortType,
    }));
    setPages(prev => ({ ...prev, [activeTab]: 1 }));

    setIsLoading(true);
    const filterToUse =
      projectIdsByTab[activeTab] && projectIdsByTab[activeTab]!.length > 0 ? projectIdsByTab[activeTab] : undefined;
    const noRelatedProjectToUse = noRelatedProjectByTab[activeTab] || false;
    fetchCertificationData(getFilterStatus(activeTab), 1, newSortType, filterToUse, noRelatedProjectToUse).finally(
      () => {
        setIsLoading(false);
      },
    );
  };

  const handleFilterChange = (selectedProjectIds: string[], noRelatedProject: boolean = false) => {
    const filterToUse = selectedProjectIds.length > 0 ? selectedProjectIds : undefined;
    setProjectIdsByTab(prev => ({ ...prev, [activeTab]: filterToUse }));
    setNoRelatedProjectByTab(prev => ({ ...prev, [activeTab]: noRelatedProject }));
    setPages(prev => ({ ...prev, [activeTab]: 1 }));

    setIsLoading(true);
    fetchCertificationData(getFilterStatus(activeTab), 1, sortTypes[activeTab], filterToUse, noRelatedProject).finally(
      () => {
        setIsLoading(false);
      },
    );
  };

  const certificationCounts: CertificationCounts = {
    all:
      (myCertificationsData?.required?.totalResults || 0) +
      (myCertificationsData?.current?.totalResults || 0) +
      (myCertificationsData?.expired?.totalResults || 0),
    required: myCertificationsData?.required?.totalResults || 0,
    expired: myCertificationsData?.expired?.totalResults || 0,
    current: myCertificationsData?.current?.totalResults || 0,
  };

  const getTotalResults = () => {
    if (activeTab === TabIndex.REQUIRED) {
      return myCertificationsData?.required?.totalResults || 0;
    } else if (activeTab === TabIndex.CURRENT) {
      return myCertificationsData?.current?.totalResults || 0;
    } else {
      return myCertificationsData?.expired?.totalResults || 0;
    }
  };

  const paginationInfo = {
    totalResults: getTotalResults(),
    pageSize: itemsPerPage,
    currentPage: pages[activeTab],
  };

  return {
    certificationList,
    requiredCertificateList,
    certificationCounts,
    isLoading,
    error,
    activeTab,
    page: pages[activeTab],
    handleTabChange,
    handlePageChange,
    paginationInfo,
    handleSortChange,
    sortType: sortTypes[activeTab],
    isWorker,
    handleFilterChange,
  };
};
